import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import en from "./translations/en.json" with { type: "json" }
import es from "./translations/es.json" with { type: "json" }
import ptBR from "./translations/pt-br.json" with { type: "json" }

export type Language = "pt-br" | "en" | "es"

const translations = {
  "pt-br": ptBR,
  en,
  es,
} as const

type TranslationValue = string | string[] | Record<string, unknown>

interface I18nContextType {
  language: Language
  setLanguage: (lang: Language) => void
  t: (key: string) => string
  tArray: (key: string) => string[]
}

const I18nContext = createContext<I18nContextType | null>(null)

const STORAGE_KEY = "tessel-site-language"
const SHARED_LOCALE_COOKIE = "tessel-locale"

/**
 * Sets a cookie that can be read by the web app.
 * Uses the parent domain so both site and app can share it.
 */
function setSharedLocaleCookie(locale: Language) {
  const expiryDate = new Date()
  expiryDate.setFullYear(expiryDate.getFullYear() + 1)

  // Get parent domain (e.g., ".tesselapp.com" from "site.tesselapp.com")
  const hostname = window.location.hostname
  const parts = hostname.split(".")
  const parentDomain =
    parts.length > 2 ? `.${parts.slice(-2).join(".")}` : hostname

  // Set cookie with parent domain so web app can read it
  document.cookie = `${SHARED_LOCALE_COOKIE}=${locale}; expires=${expiryDate.toUTCString()}; path=/; domain=${parentDomain}; SameSite=Lax`
}

function getNestedValue(
  obj: Record<string, unknown>,
  path: string,
): TranslationValue | undefined {
  const keys = path.split(".")
  let current: unknown = obj

  for (const key of keys) {
    if (current && typeof current === "object" && key in current) {
      current = (current as Record<string, unknown>)[key]
    } else {
      return undefined
    }
  }

  return current as TranslationValue
}

function detectBrowserLanguage(): Language {
  const browserLang = navigator.language.toLowerCase()

  if (browserLang.startsWith("pt")) {
    return "pt-br"
  }
  if (browserLang.startsWith("es")) {
    return "es"
  }
  if (browserLang.startsWith("en")) {
    return "en"
  }

  return "pt-br" // default
}

function getInitialLanguage(): Language {
  // Check localStorage first
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored && (stored === "pt-br" || stored === "en" || stored === "es")) {
    return stored as Language
  }

  // Detect from browser
  return detectBrowserLanguage()
}

const HTML_LANG: Record<Language, string> = {
  "pt-br": "pt-BR",
  en: "en",
  es: "es",
}

/** Syncs index.html fallbacks with active locale (see comment in index.html). */
function syncDocumentMeta(lang: Language, t: (key: string) => string): void {
  document.documentElement.lang = HTML_LANG[lang]
  document.title = t("meta.title")

  const description = document.querySelector('meta[name="description"]')
  if (description) {
    description.setAttribute("content", t("meta.description"))
  }
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>("pt-br")
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    const initialLang = getInitialLanguage()
    setLanguageState(initialLang)
    // Set shared cookie on initial load so web app has it
    setSharedLocaleCookie(initialLang)
    setMounted(true)
  }, [])

  const t = useCallback(
    (key: string): string => {
      const value = getNestedValue(
        translations[language] as Record<string, unknown>,
        key,
      )
      if (typeof value === "string") {
        return value
      }
      console.warn(`Translation key "${key}" not found or not a string`)
      return key
    },
    [language],
  )

  useEffect(() => {
    if (!mounted) {
      return
    }
    syncDocumentMeta(language, t)
  }, [language, mounted, t])

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang)
    localStorage.setItem(STORAGE_KEY, lang)
    // Also set shared cookie so web app can read it
    setSharedLocaleCookie(lang)
  }, [])

  const tArray = useCallback(
    (key: string): string[] => {
      const value = getNestedValue(
        translations[language] as Record<string, unknown>,
        key,
      )
      if (Array.isArray(value)) {
        return value as string[]
      }
      console.warn(`Translation key "${key}" not found or not an array`)
      return []
    },
    [language],
  )

  // Prevent hydration mismatch by showing nothing until mounted
  if (!mounted) {
    return null
  }

  return (
    <I18nContext.Provider value={{ language, setLanguage, t, tArray }}>
      {children}
    </I18nContext.Provider>
  )
}

export function useI18n() {
  const context = useContext(I18nContext)
  if (!context) {
    throw new Error("useI18n must be used within an I18nProvider")
  }
  return context
}

export const languageNames: Record<Language, string> = {
  "pt-br": "Português",
  en: "English",
  es: "Español",
}

export const languageFlags: Record<Language, string> = {
  "pt-br": "🇧🇷",
  en: "🇺🇸",
  es: "🇪🇸",
}
